import { ConditionWrapper, Grid } from '@approvalmax/ui/src/components';
import { constants } from 'modules/common';
import { domain } from 'modules/data';
import { useSelector } from 'modules/react-redux';
import { updateQBooksFormWithContext, uploadAttachments } from 'pages/requestForm/actions';
import { memo, useCallback, useEffect } from 'react';
import { useDispatch } from 'react-redux';
import { useUserBetaFeatures } from 'shared/hooks';

import { AttachmentsSection, RequesterInstructionSection } from '../../../components';
import FileDropProvider from '../../../components/FileDropProvider';
import SendToSupplierFileDropArea from '../../../components/SendToSupplierFileDropArea';
import { AttachmentKind } from '../../../data/AttachmentKind';
import { getContext } from '../../../selectors/pageSelectors';
import { getQBooksContext } from '../../../selectors/qbooks';
import ActionsBlock from '../../form/ActionsBlock/ActionsBlock';
import FilesSection from '../../form/FilesSection';
import { QBooksFooterLegacy } from '../(legacy)/QBooksFooterLegacy/QBooksFooterLegacy';
import { QBooksLineItemsSection } from '../QBooksLineItemsSection/QBooksLineItemsSection';
import { QBooksRequestItemsSummary } from '../QBooksRequestItemsSummary/QBooksRequestItemsSummary';
import { SalesInvoiceShippingDetailsSection } from '../SalesInvoiceShippingDetailsSection/SalesInvoiceShippingDetailsSection';
import {
    QBooksEmailToCustomerSection,
    SalesInvoiceCustomerSection,
    SalesInvoiceCustomFields,
    SalesInvoiceFieldsSection,
    SalesInvoiceMessagesSection,
    SalesInvoiceShippingSection,
} from './components';
import { messages } from './QBooksSalesInvoiceForm.messages';
import { Content, InnerContent, Root, Toolbar, ToolbarHeaderText } from './QBooksSalesInvoiceForm.styles';
import { QBooksSalesInvoiceFormProps } from './QBooksSalesInvoiceForm.types';

const { qBooksConstants } = constants;

export const QBooksSalesInvoiceForm = memo<QBooksSalesInvoiceFormProps>((props) => {
    const { request } = props;

    const dispatch = useDispatch();
    const context = useSelector(getContext);
    const qBooksContext = useSelector(getQBooksContext);
    const { details } = request;

    const supplierEmailSettings = qBooksContext.supplierEmailSettings;
    const isEmailToCustomerEnabled =
        supplierEmailSettings?.state !== domain.TemplateSettingsEmailToSupplierState.Disabled;

    const isEmailToCustomerReadonly =
        details.emailToContact !== null &&
        details.emailToContact?.emailHasToBeSent &&
        supplierEmailSettings?.state === domain.TemplateSettingsEmailToSupplierState.Disabled;

    const showEmailToCustomer = isEmailToCustomerEnabled || isEmailToCustomerReadonly;

    const onUploadAttachments = useCallback(
        (files: File[], attachmentKind: AttachmentKind) => {
            dispatch(uploadAttachments({ files, attachmentKind }));
        },
        [dispatch]
    );

    useEffect(() => {
        dispatch(updateQBooksFormWithContext());
    }, [dispatch, qBooksContext]);

    const {
        isBelgradeUIFilesUploadSection,
        isBelgradeUIEmailToSupplier,
        isBelgradeUIShippingDetailsSection,
        isBelgradeUILineItemsTotalSection,
    } = useUserBetaFeatures();

    return (
        <ConditionWrapper
            condition={
                !isBelgradeUIFilesUploadSection && !isBelgradeUIEmailToSupplier && !isBelgradeUIShippingDetailsSection
            }
            wrapperIf={(children) => (
                <FileDropProvider
                    onUpload={onUploadAttachments}
                    maxSize={context.attachmentMaxSize}
                    allowedFileExtensions={qBooksConstants.QBO_ALLOWED_FILE_EXTENSIONS}
                >
                    {children}

                    <SendToSupplierFileDropArea isSupplierAreaActive={details.emailToContact?.emailHasToBeSent} />
                </FileDropProvider>
            )}
        >
            <Root>
                <Toolbar>
                    <ToolbarHeaderText>{messages.headerText}</ToolbarHeaderText>

                    <ActionsBlock />
                </Toolbar>

                <Content>
                    <InnerContent>
                        <RequesterInstructionSection instructionText={context.requesterInstruction} />

                        <SalesInvoiceCustomerSection />

                        <SalesInvoiceFieldsSection />

                        {isBelgradeUIShippingDetailsSection ? (
                            <SalesInvoiceShippingDetailsSection />
                        ) : (
                            <SalesInvoiceShippingSection />
                        )}

                        <SalesInvoiceCustomFields />

                        <QBooksLineItemsSection />

                        {isBelgradeUILineItemsTotalSection ? <QBooksRequestItemsSummary /> : <QBooksFooterLegacy />}

                        {isBelgradeUIFilesUploadSection && (
                            <Grid gridTemplateColumns='50%' gap={16}>
                                <AttachmentsSection
                                    request={request}
                                    extensions={qBooksConstants.qboAllowedFileExtensions}
                                />
                            </Grid>
                        )}

                        <SalesInvoiceMessagesSection />

                        {!isBelgradeUIFilesUploadSection && <FilesSection />}

                        {showEmailToCustomer && (
                            <QBooksEmailToCustomerSection request={request} isReadonly={isEmailToCustomerReadonly} />
                        )}
                    </InnerContent>
                </Content>
            </Root>
        </ConditionWrapper>
    );
});

QBooksSalesInvoiceForm.displayName = 'QBooksSalesInvoiceForm';
